import api from '../../lib/api';
import type {
  ContextChange,
  ContextChangePayload,
  ContextSource,
  ContextTrend,
  InterestedParty,
  InterestedPartyPayload,
  SwotEntry,
  SwotPayload,
  PartyType,
  SwotCategory,
} from './types';
import type { EvaluationMethod, RiskEntry } from '../risks/types';

export interface ListSwotParams {
  category?: SwotCategory;
  active?: boolean;
}

export interface ListPartiesParams {
  type?: PartyType;
  active?: boolean;
}

export interface ListContextChangesParams {
  source?: ContextSource;
  trend?: ContextTrend;
  active?: boolean;
}

export interface SourceRiskLinks {
  byPartyId: Record<string, string>;
  byContextId: Record<string, string>;
}

export const fetchSourceRiskLinks = async (): Promise<SourceRiskLinks> => {
  const { data } = await api.get<SourceRiskLinks>('context/source-risk-links');
  return data;
};

export const fetchSwotEntries = async (params: ListSwotParams = {}): Promise<SwotEntry[]> => {
  const { data } = await api.get<SwotEntry[]>('context/swot', { params });
  return data;
};

export const createSwotEntry = async (payload: SwotPayload): Promise<SwotEntry> => {
  const { data } = await api.post<SwotEntry>('context/swot', payload);
  return data;
};

export const updateSwotEntry = async (id: string, payload: Partial<SwotPayload>): Promise<SwotEntry> => {
  const { data } = await api.put<SwotEntry>(`context/swot/${id}`, payload);
  return data;
};

export const deactivateSwotEntry = async (id: string): Promise<SwotEntry> => {
  const { data } = await api.delete<SwotEntry>(`context/swot/${id}`);
  return data;
};

export const promoteSwotEntry = async (id: string): Promise<RiskEntry> => {
  const { data } = await api.post<RiskEntry>(`context/swot/${id}/promote`);
  return data;
};

export const fetchInterestedParties = async (
  params: ListPartiesParams = {},
): Promise<InterestedParty[]> => {
  const { data } = await api.get<InterestedParty[]>('context/interested-parties', { params });
  return data;
};

export const createInterestedParty = async (
  payload: InterestedPartyPayload,
): Promise<InterestedParty> => {
  const { data } = await api.post<InterestedParty>('context/interested-parties', payload);
  return data;
};

export const updateInterestedParty = async (
  id: string,
  payload: Partial<InterestedPartyPayload>,
): Promise<InterestedParty> => {
  const { data } = await api.put<InterestedParty>(`context/interested-parties/${id}`, payload);
  return data;
};

export const deactivateInterestedParty = async (id: string): Promise<InterestedParty> => {
  const { data } = await api.delete<InterestedParty>(`context/interested-parties/${id}`);
  return data;
};

export const promoteInterestedParty = async (id: string): Promise<RiskEntry> => {
  const { data } = await api.post<RiskEntry>(`context/interested-parties/${id}/promote`);
  return data;
};

export const fetchContextChanges = async (
  params: ListContextChangesParams = {},
): Promise<ContextChange[]> => {
  const { data } = await api.get<ContextChange[]>('context-changes', { params });
  return data;
};

export const createContextChange = async (payload: ContextChangePayload): Promise<ContextChange> => {
  const { data } = await api.post<ContextChange>('context-changes', payload);
  return data;
};

export const updateContextChange = async (
  id: string,
  payload: Partial<ContextChangePayload & { active?: boolean }>,
): Promise<ContextChange> => {
  const { data } = await api.put<ContextChange>(`context-changes/${id}`, payload);
  return data;
};

export const deleteContextChange = async (id: string): Promise<void> => {
  await api.delete(`context-changes/${id}`);
};

export const promoteContextChange = async (id: string): Promise<RiskEntry> => {
  const { data } = await api.post<RiskEntry>(`context/context-changes/${id}/promote`);
  return data;
};

export const fetchEvaluationMethods = async (): Promise<EvaluationMethod[]> => {
  const { data } = await api.get<EvaluationMethod[]>('evaluation-methods');
  return data;
};
